// ============================================================================
//  HR Module — Congés
//  LeavesPage · LeaveCalendarPage · LeaveRequestPage
// ============================================================================

import React, { useState, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  Calendar,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Filter,
  Users,
  Search,
  FileText,
  Download,
  RefreshCw,
} from 'lucide-react'
import { hrApi, type LeaveRequest, type LeaveBalance } from '@/modules/hr/api/hr.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore } from '@/stores/auth.store'

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short', year: 'numeric' })
const fmtShort = (d: string) =>
  new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short' })

const LEAVE_STATUS: Record<
  string,
  { label: string; color: string; bg: string; icon: React.ElementType }
> = {
  PENDING: {
    label: 'En attente',
    color: 'text-[#D97706]',
    bg: 'bg-[#FEF3C7] border-[rgba(245,158,11,0.25)]',
    icon: Clock,
  },
  MANAGER_APPROVED: {
    label: 'Manager approuvé',
    color: 'text-[var(--blue-d)]',
    bg: 'bg-[#E0F2FE] border-[rgba(245,124,0,0.25)]',
    icon: CheckCircle,
  },
  HR_APPROVED: {
    label: 'RH approuvé',
    color: 'text-[#7C3AED]',
    bg: 'bg-[#EDE9FE] border-[rgba(124,58,237,0.25)]',
    icon: CheckCircle,
  },
  DIRECTION_APPROVED: {
    label: 'Direction approuvé',
    color: 'text-[#0369A1]',
    bg: 'bg-[#E0F2FE] border-[rgba(3,105,161,0.25)]',
    icon: CheckCircle,
  },
  APPROVED: {
    label: 'Approuvé',
    color: 'text-[#059669]',
    bg: 'bg-[#10B981]/10 border-[rgba(16,185,129,0.25)]',
    icon: CheckCircle,
  },
  REJECTED: {
    label: 'Rejeté',
    color: 'text-[#DC2626]',
    bg: 'bg-[#FEF2F2] border-[rgba(239,68,68,0.25)]',
    icon: XCircle,
  },
  CANCELLED: {
    label: 'Annulé',
    color: 'text-[var(--tx3)]',
    bg: 'bg-[#D6D2EC]/10 border-[var(--tx3)]/20',
    icon: XCircle,
  },
}

// ============================================================================
//  LEAVES PAGE — Tableau de bord RH
// ============================================================================

// ─── Decimal.js helper ───────────────────────────────────────────────────────
const toNum = (v: any): number => {
  if (v === null || v === undefined) return 0
  if (typeof v === 'number') return v
  if (typeof v === 'string') return parseFloat(v) || 0
  if (typeof v === 'object' && Array.isArray(v.d) && typeof v.e === 'number' && v.d.length > 0) {
    const sign = v.s < 0 ? -1 : 1
    const d0 = v.d[0]
    const exp = v.e - String(d0).length + 1
    return sign * d0 * Math.pow(10, exp)
  }
  return Number(v) || 0
}

// ─── KpiCard ──────────────────────────────────────────────────────────────────
function KpiCard({
  label,
  value,
  icon: Icon,
  color,
  iconBg,
}: {
  label: string
  value: number | string
  icon: any
  color: string
  iconBg: string
}) {
  return (
    <div className="bg-white border border-[var(--bd)] rounded-xl p-4 shadow-sm flex items-center gap-4">
      <div
        style={{ background: iconBg }}
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
      >
        <Icon style={{ color }} className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-[var(--brand-dark)]">{value}</p>
        <p className="text-xs text-[var(--tx3)] mt-0.5">{label}</p>
      </div>
    </div>
  )
}

export default function LeavesPage() {
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showApproveModal, setShowApproveModal] = useState<{
    id: string
    action: 'approve' | 'reject'
  } | null>(null)
  const [comment, setComment] = useState('')
  const [impactLeaveId, setImpactLeaveId] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)
  const user = useAuthStore((s) => s.user)

  const { data, isLoading } = useQuery({
    queryKey: ['leaves', { status, search, page }],
    queryFn: () => hrApi.leaves.list({ status: status || undefined, page, limit: 20 }),
    placeholderData: (prev) => prev,
  })

  const { data: stats } = useQuery({
    queryKey: ['leaveStats'],
    queryFn: () => hrApi.leaves.getStats(),
  })

  const mutOpts = (successMsg: string) => ({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaves'] })
      setShowApproveModal(null)
      setComment('')
      addNotification({ type: 'success', title: 'Congé', message: successMsg })
    },
    onError: (err: any) =>
      addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message }),
  })

  const { mutate: approveHr } = useMutation({
    mutationFn: (id: string) => hrApi.leaves.approveByHr(id, comment),
    ...mutOpts('Congé approuvé par les RH.'),
  })
  const { mutate: reject } = useMutation({
    mutationFn: (id: string) => hrApi.leaves.reject(id, comment),
    ...mutOpts('Congé rejeté.'),
  })

  const [showInitModal, setShowInitModal] = useState(false)
  const [initYear, setInitYear] = useState(new Date().getFullYear())
  const queryClient2 = useQueryClient()
  const { mutate: initBalances, isPending: isIniting } = useMutation({
    mutationFn: () => hrApi.leaves.initializeBalances(initYear),
    onSuccess: (res: any) => {
      queryClient2.invalidateQueries({ queryKey: ['leaves'] })
      setShowInitModal(false)
      addNotification({ type: 'success', title: 'Soldes initialisés', message: res?.message ?? 'Soldes créés pour ' + initYear })
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message }),
  })

  const requests = data?.requests ?? []

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Congés</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Gestion des demandes de congé</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowInitModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-[var(--bd)] hover:bg-[var(--surface-2)] text-sm font-medium text-[var(--tx2)] rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Init. soldes
          </button>
          <Link
            to="/hr/leaves/calendar"
            className="flex items-center gap-2 px-4 py-2 bg-white border border-[var(--bd)] hover:bg-[#F0F4FF] text-sm font-medium text-[var(--tx2)] rounded-lg transition-colors shadow-sm"
          >
            <Calendar className="w-4 h-4" />
            Calendrier
          </Link>
          <Link
            to="/hr/leaves/request"
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-sm text-white font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouvelle demande
          </Link>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 nx-stagger">
          <KpiCard
            label="En attente"
            value={stats.pendingApproval}
            icon={Clock}
            color="#D97706"
            iconBg="#FEF3C7"
          />
          <KpiCard
            label="Approuvés ce mois"
            value={stats.byStatus?.find((s: any) => s.status === 'APPROVED')?._count?.id ?? 0}
            icon={CheckCircle}
            color="#059669"
            iconBg="#ECFDF5"
          />
          <KpiCard
            label="Rejetés ce mois"
            value={stats.byStatus?.find((s: any) => s.status === 'REJECTED')?._count?.id ?? 0}
            icon={XCircle}
            color="#DC2626"
            iconBg="#FEF2F2"
          />
          <KpiCard
            label="Jours pris (mois)"
            value={
              stats.byType?.reduce((a: number, b: any) => a + Number(b._sum?.totalDays ?? 0), 0) ??
              0
            }
            icon={Calendar}
            color="var(--blue-d)"
            iconBg="#E0F2FE"
          />
        </div>
      )}

      {/* Filtres */}
      <div className="flex gap-3">
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            setPage(1)
          }}
          className="px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(LEAVE_STATUS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <div className="w-6 h-6 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-[var(--tx3)]">
            <Calendar className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-sm">Aucune demande</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Employé', 'Type', 'Période', 'Jours', 'Statut', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-[var(--tx3)] uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {requests.map((req) => {
                const cfg = LEAVE_STATUS[req.status]
                const Icon = cfg?.icon
                return (
                  <tr key={req.id} className="hover:bg-[var(--surface-2)] transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-[var(--brand-dark)]">
                          {req.employee?.firstName} {req.employee?.lastName}
                        </p>
                        <p className="text-xs text-[var(--tx3)]">{req.employee?.matricule}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--tx2)]">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ background: req.leaveType?.color ?? '#64748b' }}
                        />
                        {req.leaveType?.name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--tx2)]">
                      {fmtShort(req.startDate)} → {fmtShort(req.endDate)}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-[var(--brand-dark)]">
                      {toNum(req.totalDays)}j
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg?.bg} ${cfg?.color}`}
                      >
                        {Icon && <Icon className="w-3 h-3" />}
                        {cfg?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {/* Niveau 1-2 : RH peut approuver PENDING ou MANAGER_APPROVED */}
                      {['PENDING', 'MANAGER_APPROVED'].includes(req.status) && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setShowApproveModal({ id: req.id, action: 'approve' })}
                            className="p-1.5 hover:bg-[#10B981]/10 text-[#059669] rounded-lg transition-colors"
                            title="Valider (RH)"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setShowApproveModal({ id: req.id, action: 'reject' })}
                            className="p-1.5 hover:bg-[#FEF2F2] text-[#DC2626] rounded-lg transition-colors"
                            title="Rejeter"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      {/* Sprint 2 — PDF fiche absence + Impact projet */}
                      <div className="flex items-center gap-1 mt-1">
                        <button
                          onClick={async (e) => { e.stopPropagation(); try { const r = await hrApi.generateAbsencePdf(req.id); if (r?.url) window.open(r.url, '_blank') } catch {} }}
                          className="p-1 hover:bg-[var(--surface-2)] rounded text-[var(--tx3)] hover:text-[var(--brand-orange)]"
                          title="Télécharger fiche PDF"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setImpactLeaveId(req.id)}
                          className="p-1 hover:bg-[var(--surface-2)] rounded text-[var(--tx3)] hover:text-[var(--blue)]"
                          title="Impact projets"
                        >
                          <AlertTriangle className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Approbation/Rejet */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white border border-[var(--bd)] rounded-2xl p-6 w-full max-w-2xl shadow-2xl">
            <h3 className="text-lg font-bold text-[var(--brand-dark)] mb-4">
              {showApproveModal.action === 'approve' ? '✅ Valider (RH)'
                : '❌ Rejeter'} la demande
            </h3>
            <div>
              <label className="block text-xs text-[var(--tx3)] mb-1.5">
                {showApproveModal.action === 'reject'
                  ? 'Motif de rejet (obligatoire)'
                  : 'Commentaire (optionnel)'}
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  showApproveModal.action === 'reject' ? 'Motif du rejet…' : 'Commentaire…'
                }
                rows={3}
                className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] resize-none"
              />
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setShowApproveModal(null)
                  setComment('')
                }}
                className="flex-1 py-2 text-sm text-[var(--tx3)] border border-[var(--bd)] rounded-lg hover:text-[var(--brand-dark)] transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  if (showApproveModal.action === 'approve') approveHr(showApproveModal.id)
                  else reject(showApproveModal.id)
                }}
                disabled={showApproveModal.action === 'reject' && comment.length < 5}
                className={`flex-1 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 ${
                  showApproveModal.action === 'reject'
                    ? 'bg-red-600 hover:bg-red-500'
                    : 'bg-emerald-600 hover:bg-[#10B981]'
                }`}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
      {showInitModal && (
        <div className="fixed inset-0 bg-[rgba(22,12,38,0.45)] backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white border border-[var(--bd)] rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-[var(--tx)]">Initialiser les soldes de congés</h3>
              <button onClick={() => setShowInitModal(false)} className="text-[var(--tx3)] hover:text-[var(--tx)]">✕</button>
            </div>
            <p className="text-sm text-[var(--tx3)] mb-4">Crée les soldes annuels pour tous les employés actifs. À effectuer en début d'année.</p>
            <div className="mb-5">
              <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Année</label>
              <input type="number" value={initYear} onChange={e => setInitYear(+e.target.value)}
                className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowInitModal(false)} className="flex-1 py-2 bg-[var(--surface-2)] text-[var(--tx2)] rounded-lg text-sm">Annuler</button>
              <button onClick={() => initBalances()} disabled={isIniting}
                className="flex-1 py-2 bg-[var(--blue)] text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                {isIniting ? 'En cours…' : 'Initialiser ' + initYear}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sprint 2 — Modal Impact Projet */}
      {impactLeaveId && (
        <ImpactProjetModal leaveId={impactLeaveId} onClose={() => setImpactLeaveId(null)} />
      )}
    </div>
  )
}

function ImpactProjetModal({ leaveId, onClose }: { leaveId: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['leave-impact', leaveId],
    queryFn: () => hrApi.getAbsenceProjectImpact(leaveId),
  })

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-[var(--tx)] mb-1">Impact sur les projets</h3>
        <p className="text-sm text-[var(--tx3)] mb-4">Projets et tâches affectés par cette absence</p>

        {isLoading ? (
          <div className="text-center py-8 text-[var(--tx3)]">Analyse en cours…</div>
        ) : !data?.impactedProjects?.length ? (
          <div className="text-center py-8 text-green-600">
            <CheckCircle className="w-8 h-8 mx-auto mb-2" />
            <p className="font-medium">Aucun projet impacté</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4 mb-4 p-3 bg-amber-50 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">{data.totalProjectsImpacted}</div>
                <div className="text-xs text-amber-700">projet(s)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-500">{data.totalTasksAtRisk}</div>
                <div className="text-xs text-red-600">tâche(s) à risque</div>
              </div>
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {data.impactedProjects.map((p: any) => (
                <div key={p.project.id} className="border border-[var(--bd)] rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm text-[var(--tx)]">{p.project.name}</span>
                    <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">{p.activeTasksDuringAbsence} tâche(s)</span>
                  </div>
                  <div className="text-xs text-[var(--tx3)] mt-1">Rôle : {p.role} · Période : {p.absencePeriod.days}j</div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="flex justify-end mt-4 pt-3 border-t border-[var(--bd)]">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-[var(--surface-2)] rounded-lg hover:bg-[var(--bd)]">Fermer</button>
        </div>
      </div>
    </div>
  )
}

export function LeaveCalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date())
  const navigate = useNavigate()

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  // Début et fin du mois affiché
  const startDate = new Date(year, month, 1).toISOString().split('T')[0]
  const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0]

  const { data: events } = useQuery({
    queryKey: ['leaveCalendar', startDate, endDate],
    queryFn: () => hrApi.leaves.getCalendar({ startDate, endDate }),
  })

  const MONTHS = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
  ]
  const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

  // Jours du mois
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7 // Lundi = 0

  const calendarDays = useMemo(() => {
    const days: (number | null)[] = Array(firstDayOfWeek).fill(null)
    for (let d = 1; d <= daysInMonth; d++) days.push(d)
    // Compléter jusqu'à 42 cases (6 semaines)
    while (days.length < 42) days.push(null)
    return days
  }, [year, month, daysInMonth, firstDayOfWeek])

  // Indexer les événements par jour
  const eventsByDay = useMemo(() => {
    const map = new Map<number, typeof events>()
    for (const evt of events ?? []) {
      const start = new Date(evt.startDate)
      const end = new Date(evt.endDate)
      // Parcourir tous les jours de l'événement dans ce mois
      const cur = new Date(Math.max(start.getTime(), new Date(year, month, 1).getTime()))
      const fin = new Date(Math.min(end.getTime(), new Date(year, month + 1, 0).getTime()))
      while (cur <= fin) {
        const d = cur.getDate()
        if (!map.has(d)) map.set(d, [])
        map.get(d)!.push(evt)
        cur.setDate(cur.getDate() + 1)
      }
    }
    return map
  }, [events, year, month])

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-[var(--tx3)]" />
          </button>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Calendrier équipe</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
            className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-[var(--tx3)]" />
          </button>
          <span className="text-base font-semibold text-[var(--brand-dark)] w-40 text-center">
            {MONTHS[month]} {year}
          </span>
          <button
            onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
            className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-[var(--tx3)]" />
          </button>
        </div>
        <Link
          to="/hr/leaves/request"
          className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-sm text-white font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Demander un congé
        </Link>
      </div>

      {/* Grille calendrier */}
      <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl overflow-hidden">
        {/* Entêtes jours */}
        <div className="grid grid-cols-7 border-b border-[var(--bd)]">
          {DAYS.map((d) => (
            <div
              key={d}
              className="p-3 text-center text-xs font-medium text-[var(--tx3)] uppercase tracking-wider"
            >
              {d}
            </div>
          ))}
        </div>

        {/* Cases */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, i) => {
            const isToday =
              day === new Date().getDate() &&
              month === new Date().getMonth() &&
              year === new Date().getFullYear()
            const dayEvents = day ? (eventsByDay.get(day) ?? []) : []
            return (
              <div
                key={i}
                className={`min-h-24 p-2 border-b border-r border-[var(--bd)] ${day ? 'hover:bg-[var(--surface-2)]' : 'opacity-30'} transition-colors`}
              >
                {day && (
                  <>
                    <span
                      className={`text-sm font-medium ${isToday ? 'w-6 h-6 rounded-full bg-[var(--blue)] text-white flex items-center justify-center' : 'text-[var(--tx2)]'}`}
                    >
                      {day}
                    </span>
                    <div className="mt-1 space-y-1">
                      {dayEvents.slice(0, 3).map((evt, j) => (
                        <div
                          key={j}
                          className="text-xs rounded px-1 py-0.5 truncate font-medium"
                          style={{
                            background: (evt.leaveType?.color ?? '#3b82f6') + '33',
                            color: evt.leaveType?.color ?? '#3b82f6',
                          }}
                        >
                          {evt.employee?.firstName} {evt.employee?.lastName?.[0]}.
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <p className="text-xs text-[var(--tx3)]">+{dayEvents.length - 3} autres</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Légende */}
      <div className="flex flex-wrap gap-3">
        {Array.from(new Set(events?.map((e) => e.leaveTypeId))).map((ltId) => {
          const evt = events?.find((e) => e.leaveTypeId === ltId)
          if (!evt) return null
          return (
            <span key={ltId} className="flex items-center gap-2 text-xs text-[var(--tx3)]">
              <span
                className="w-3 h-3 rounded"
                style={{ background: evt.leaveType?.color ?? '#64748b' }}
              />
              {evt.leaveType?.name}
            </span>
          )
        })}
      </div>
    </div>
  )
}

// ============================================================================
//  LEAVE REQUEST PAGE — Demande employé
// ============================================================================

export function LeaveRequestPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)
  const { user } = useAuthStore()

  const [form, setForm] = useState({
    leaveTypeId: '',
    startDate: '',
    endDate: '',
    reason: '',
    documentUrl: '',
  })

  const { data: leaveTypes } = useQuery({
    queryKey: ['leaveTypes'],
    queryFn: () => hrApi.leaves.getTypes(),
  })
  const { data: balances } = useQuery({
    queryKey: ['myBalances'],
    queryFn: () => hrApi.leaves.getBalances(user!.employeeId!),
    enabled: !!user?.employeeId,
  })

  const selectedType = leaveTypes?.find((lt) => lt.id === form.leaveTypeId)
  const balance = balances?.find((b) => b.leaveTypeId === form.leaveTypeId)

  // Calculer les jours
  const workingDays = useMemo(() => {
    if (!form.startDate || !form.endDate) return 0
    const start = new Date(form.startDate)
    const end = new Date(form.endDate)
    if (end < start) return 0
    let days = 0
    const cur = new Date(start)
    while (cur <= end) {
      const dow = cur.getDay()
      if (dow !== 0 && dow !== 6) days++
      cur.setDate(cur.getDate() + 1)
    }
    return days
  }, [form.startDate, form.endDate])

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      hrApi.leaves.create({
        ...form,
        employeeId: user!.employeeId!,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaves'] })
      addNotification({
        type: 'success',
        title: 'Demande envoyée',
        message: 'Votre demande de congé a été soumise.',
      })
      navigate('/hr/leaves')
    },
    onError: (err: any) =>
      addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message }),
  })

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const canSubmit =
    form.leaveTypeId &&
    form.startDate &&
    form.endDate &&
    workingDays > 0 &&
    (!selectedType?.requiresDoc || form.documentUrl)

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-[var(--tx3)]" />
        </button>
        <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Demande de congé</h1>
      </div>

      <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl p-6 space-y-5">
        {/* Type de congé */}
        <div>
          <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
            Type de congé <span className="text-[#DC2626]">*</span>
          </label>
          <select
            value={form.leaveTypeId}
            onChange={(e) => set('leaveTypeId', e.target.value)}
            className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
          >
            <option value="">Sélectionner…</option>
            {leaveTypes?.map((lt) => (
              <option key={lt.id} value={lt.id}>
                {lt.name} ({lt.isPaid ? 'Payé' : 'Non payé'})
              </option>
            ))}
          </select>
        </div>

        {/* Solde */}
        {balance && (
          <div
            className="flex items-center justify-between p-3 rounded-lg"
            style={{ background: (selectedType?.color ?? '#3b82f6') + '15' }}
          >
            <span className="text-sm text-[var(--tx2)]">Solde disponible</span>
            <span className="text-lg font-bold text-[var(--brand-dark)]">
              {toNum(balance.remaining)} jour{toNum(balance.remaining) > 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
              Date de début <span className="text-[#DC2626]">*</span>
            </label>
            <input
              type="date"
              value={form.startDate}
              onChange={(e) => set('startDate', e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
              Date de fin <span className="text-[#DC2626]">*</span>
            </label>
            <input
              type="date"
              value={form.endDate}
              onChange={(e) => set('endDate', e.target.value)}
              min={form.startDate || new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
            />
          </div>
        </div>

        {/* Indicateur jours */}
        {workingDays > 0 && (
          <div
            className={`flex items-center justify-between p-3 rounded-lg ${
              balance && workingDays > toNum(balance.remaining)
                ? 'bg-[#FEF2F2] border border-[rgba(239,68,68,0.25)]'
                : 'bg-[#E0F2FE] border border-[rgba(245,124,0,0.25)]'
            }`}
          >
            <span className="text-sm text-[var(--tx2)]">Jours ouvrables demandés</span>
            <span
              className={`text-lg font-bold ${balance && workingDays > toNum(balance.remaining) ? 'text-[#DC2626]' : 'text-[var(--blue-d)]'}`}
            >
              {workingDays}j
            </span>
          </div>
        )}

        {/* Préavis warning */}
        {selectedType?.noticeDaysRequired > 0 && (
          <div className="flex items-center gap-2 p-3 bg-[#FEF3C7] border border-[rgba(245,158,11,0.25)] rounded-lg">
            <AlertTriangle className="w-4 h-4 text-[#D97706] shrink-0" />
            <p className="text-xs text-amber-300">
              Ce type de congé requiert un préavis de{' '}
              <strong>{selectedType.noticeDaysRequired} jour(s)</strong>.
            </p>
          </div>
        )}

        {/* Motif */}
        <div>
          <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Motif</label>
          <textarea
            value={form.reason}
            onChange={(e) => set('reason', e.target.value)}
            placeholder="Motif de la demande…"
            rows={3}
            className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] resize-none"
          />
        </div>

        {/* Justificatif */}
        {selectedType?.requiresDoc && (
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
              Justificatif <span className="text-[#DC2626]">*</span>
              <span className="text-[var(--tx3)]"> (URL document uploadé)</span>
            </label>
            <input
              type="url"
              value={form.documentUrl}
              onChange={(e) => set('documentUrl', e.target.value)}
              placeholder="https://…"
              className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-[var(--bd)]">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--brand-dark)] transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={() => mutate()}
            disabled={!canSubmit || isPending}
            className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-sm text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? (
              <div className="w-4 h-4 border-2 border-[var(--tx3)] border-t-white rounded-full animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            Soumettre la demande
          </button>
        </div>
      </div>
    </div>
  )
}
